import { useEffect, useRef, useState, type RefObject } from 'react';

type UseInViewOnceOptions = {
  once?: boolean;
  margin?: string;
};

type RevealClassOptions = {
  threshold?: number;
  rootMargin?: string;
};

export function isSsgPrerenderRoot(): boolean {
  return (
    typeof document !== 'undefined' &&
    document.getElementById('root')?.getAttribute('data-ssg') === 'true'
  );
}

/** Lightweight IntersectionObserver — avoids importing useInView from framer-motion. */
export function useInViewOnce(
  ref: RefObject<Element | null>,
  { once = true, margin }: UseInViewOnceOptions = {}
): boolean {
  const [inView, setInView] = useState(() => isSsgPrerenderRoot());

  useEffect(() => {
    const el = ref.current;
    if (!el || (once && inView)) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setInView(true);
          if (once) observer.disconnect();
        } else if (!once) {
          setInView(false);
        }
      },
      { rootMargin: margin ?? '0px', threshold: 0 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [ref, once, margin, inView]);

  return inView;
}

/** Adds a CSS class when a section scrolls into view (logistics tiles, fulfilment centre, etc.). */
export function useRevealClass(visibleClassName: string, options: RevealClassOptions = {}) {
  const threshold = options.threshold ?? 0.08;
  const rootMargin = options.rootMargin ?? '0px 0px -32px 0px';
  const ref = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    if (isSsgPrerenderRoot()) {
      root.classList.add(visibleClassName);
      return;
    }
    if (
      typeof IntersectionObserver === 'undefined' ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      root.classList.add(visibleClassName);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add(visibleClassName);
            io.unobserve(entry.target);
          }
        });
      },
      { threshold, rootMargin }
    );
    io.observe(root);
    return () => io.disconnect();
  }, [visibleClassName, threshold, rootMargin]);

  return ref;
}

/** Staggered card/step reveal (FBM, multichannel, returns, TikTok). */
export function useStaggerReveal(count: number, threshold = 0.1, staggerMs = 90) {
  const [visible, setVisible] = useState<boolean[]>(() =>
    isSsgPrerenderRoot() ? new Array(count).fill(true) : new Array(count).fill(false)
  );
  const refs = useRef<(HTMLElement | null)[]>([]);

  useEffect(() => {
    if (isSsgPrerenderRoot()) {
      setVisible(new Array(count).fill(true));
      return;
    }
    const observers = refs.current.map((el, i) => {
      if (!el) return null;
      const obs = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setTimeout(() => {
              setVisible((prev) => {
                const next = [...prev];
                next[i] = true;
                return next;
              });
            }, i * staggerMs);
            obs.disconnect();
          }
        },
        { threshold }
      );
      obs.observe(el);
      return obs;
    });
    return () => observers.forEach((o) => o?.disconnect());
  }, [count, threshold, staggerMs]);

  return { refs, visible };
}

/** Single block in-view flag (pricing panels, etc.). */
export function useSectionInView(threshold = 0.12) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [inView, setInView] = useState(isSsgPrerenderRoot);

  useEffect(() => {
    if (isSsgPrerenderRoot()) {
      setInView(true);
      return;
    }
    if (!ref.current) return;
    const o = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          o.disconnect();
        }
      },
      { threshold }
    );
    o.observe(ref.current);
    return () => o.disconnect();
  }, [threshold]);

  return { ref, inView };
}
